import { compose, lifecycle, withHandlers } from 'recompose';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { get, map } from 'lodash';
import React from 'react';
import Money from '../components/Money';
import Loader from '../components/Loader';
import GlobalErrorAndRedirectionListener from
  '../components/GlobalErrorAndRedirectionListener';
import { getReceipt, getReceiptLoading } from './selectors';
import { formatPhone } from '../utils/helpers';
import { fetchReceipt } from './actions';
import cardLogoAmericanExpress from '../../../assets/images/receipt/card-logo-americanexpress.png';
import cardLogoDiscover from '../../../assets/images/receipt/card-logo-discover.png';
import cardLogoGeneric from '../../../assets/images/receipt/card-logo-generic.png';
import cardLogoMasterCard from '../../../assets/images/receipt/card-logo-mastercard.png';
import cardLogoVisa from '../../../assets/images/receipt/card-logo-visa.png';
import squireLogo from '../../../assets/images/receipt/squire-logo.svg';
import squireLogoMonochrome from '../../../assets/images/receipt/squire-logo-monochrome.png';
import forhimsAd from '../../../assets/images/receipt/ad/forhims.png';

const ReceiptTip = ({ tip }) => (
  <div>
    <ReceiptItem name="Tip" amount={tip} alwaysShow />
    <hr className="c-receipt__divider" />
  </div>
);
ReceiptTip.propTypes = {
  tip: PropTypes.number.isRequired,
};
ReceiptTip.defaultProps = {
  tip: 0,
};

const ReceiptType = ({ terminalId, last4 }) => {
  if (terminalId) {
    return (
      <div className="o-tableize__content c-receipt__note">
        Cardholder Payment Receipt
      </div>
    );
  }
  if (last4) {
    return (
      <div className="o-tableize__content c-receipt__note">
        Card Payment Receipt
      </div>
    );
  }
  return (
    <div className="o-tableize__content c-receipt__note">
      Cash Payment Receipt
    </div>
  );
};
ReceiptType.propTypes = {
  terminalId: PropTypes.string,
  last4: PropTypes.string,
};
ReceiptType.defaultProps = {
  terminalId: '',
  last4: '',
};

const ReceiptItem = ({ name, amount, negative, alwaysShow, total }) => {
  if (!amount && !alwaysShow) return null;
  return (
    <div className={classnames(
      'c-receipt__item',
      { 'c-receipt__item--negative': negative },
      { 'c-receipt__item--total': total }
    )}
    >
      <div>{name}</div>
      <div className={classnames({ 'c-receipt__value': total })}>
        {negative ? '-' : ''}
        <Money>{amount}</Money>
      </div>
    </div>
  );
};
ReceiptItem.propTypes = {
  name: PropTypes.string,
  amount: PropTypes.number,
  alwaysShow: PropTypes.bool,
  negative: PropTypes.bool,
  total: PropTypes.bool,
};
ReceiptItem.defaultProps = {
  name: '',
  amount: 0,
  alwaysShow: false,
  negative: false,
  total: false,
};

const cardBrandsImage = {
  'American Express': cardLogoAmericanExpress,
  Discover: cardLogoDiscover,
  MasterCard: cardLogoMasterCard,
  Visa: cardLogoVisa,
};

const year = (new Date()).getFullYear();

const ReceiptComponent = ({ receipt, loading }) => {
  const isDiscounted = receipt.discount > 0 || receipt.promo > 0;
  const logo = get(receipt, 'shop.logo') ? get(receipt, 'shop.logo.url') : squireLogo;
  const phoneFormatted = formatPhone(get(receipt, 'shop.phone', ''));
  const showMultipleTaxes = get(receipt, 'taxes', []).length > 1;

  return (
    <div className={classnames(
        'c-receipt-wrapper',
        { 'c-receipt-wrapper--ad': receipt.receiptAds }
      )}
    >
      <GlobalErrorAndRedirectionListener />
      <Helmet>
        <title>Receipt from: {get(receipt, 'shop.name', '')}</title>
      </Helmet>
      {loading && <Loader />}
      {!loading &&
      <div className="c-receipt">
        <div className="c-receipt-inner">
          <div className={classnames(
              'c-receipt__border',
              { 'c-receipt__border--ad': receipt.receiptAds }
            )}
          />
          {!!receipt.refunded &&
          <div className="c-receipt__refund">
            <div className="c-receipt__refund-text">
              Refunded
            </div>
          </div>
          }
          <div className="c-receipt__header">
            <div>
              <div className="c-receipt__header-text c-receipt__header-text--shop">
                {get(receipt, 'shop.name')}
              </div>
              <div className="c-receipt__header-text">
                {get(receipt, 'shop.address.street', '')}
                <br />{get(receipt, 'shop.address.city', '')},&nbsp;
                {get(receipt, 'shop.address.state', '')} {get(receipt, 'shop.address.zip', '')}
                <br />{get(receipt, 'shop.address.country', '')}
                <br />{phoneFormatted}
              </div>
            </div>
            <div className="c-receipt__header-logo">
              <img src={logo} alt="logo" width="95" />
            </div>
          </div>
          <div className="o-tableize">
            <div className="o-tableize__cell">
              <ReceiptType
                terminalId={receipt.terminalId}
                last4={receipt.last4}
              />
            </div>
            <div className="o-tableize__cell">
              <div className="o-tableize__content c-receipt__total">
                <Money>{receipt.total}</Money>
              </div>
            </div>
          </div>
          {map(receipt.items, (item, idx) => (
            <div key={`${item.barberName}-${idx}`}>
              <span className="c-receipt__barber">
                {item.barberName}
              </span>
              <br />
              {map(item.subItems, (subitem, subIdx) => (
                <ReceiptItem
                  key={`${subitem.name}-${subIdx}`}
                  name={`+ ${subitem.name}`}
                  amount={subitem.amount}
                />
              ))}
            </div>
          ))}
          {isDiscounted &&
          <hr className="c-receipt__divider c-receipt__divider--negative" />
          }
          <ReceiptItem name="Discount" amount={receipt.discount} negative />
          <ReceiptItem
            name={`${receipt.isLoyaltyPromo ? 'Loyalty Reward' : 'Promo'} (${receipt.promoCode})`}
            amount={receipt.promo}
            negative
          />
          <hr className={classnames('c-receipt__divider', { 'c-receipt__divider--negative': isDiscounted })} />
          {map(receipt.items, 'subItems').length > 1 && <ReceiptItem name="Subtotal" amount={receipt.subtotal} />}

          {showMultipleTaxes && map(receipt.taxes, tax => <ReceiptItem name={tax.name} amount={tax.amount} />)}
          {showMultipleTaxes && <ReceiptItem name="Fees" amount={receipt.fee} />}
          {!showMultipleTaxes && <ReceiptItem name="Taxes & Fees" amount={receipt.taxAndFee} />}
          <ReceiptTip
            tip={receipt.tip}
          />
          <ReceiptItem name="Total" amount={receipt.totalBeforeRefund} alwaysShow total />
          {!!receipt.refunded &&
          <hr className="c-receipt__divider c-receipt__divider" />
          }
          <ReceiptItem name="Refunded" amount={receipt.refunded} negative />
          <div className="c-receipt__info">
            {receipt.last4 &&
              <div className="c-receipt__info-line c-receipt__info-line--tall">
                <div>Card</div>
                <img src={cardBrandsImage[receipt.brand] || cardLogoGeneric} alt="card logo" width="26" height="26" />
              </div>
            }
            {receipt.last4 &&
              <div className="c-receipt__info-line">
                <div>Account</div>
                <div>**** **** **** {receipt.last4}</div>
              </div>
            }
            {receipt.giftCardCode &&
              <div className="c-receipt__info-line c-receipt__info-line--tall">
                <div>Gift Card</div>
                <div>{receipt.giftCardCode}</div>
              </div>
            }
            {map(receipt.paymentDetails, (value, key) => (
              <div className="c-receipt__info-line" key={key}>
                <div>{key}</div>
                <div>{value}</div>
              </div>
            ))}
          </div>
          <div className="c-receipt__note c-receipt__note--black">Please retain receipt!</div>
          <div className="o-tableize c-receipt__datetime">
            <div className="o-tableize__cell">
              <div className="o-tableize__content">
                {receipt.createdAtTime}
              </div>
            </div>
            <div className="o-tableize__cell">
              <div className="o-tableize__content u-text-right">
                {receipt.createdAtDate}
              </div>
            </div>
          </div>
          <div className="c-receipt__info c-receipt__info--small">
            {map(receipt.clearingDetails, (value, key) => (
              <div className="c-receipt__info-line" key={key}>
                <div>{key}</div>
                <div>{value}</div>
              </div>
            ))}
          </div>
          <div className={classnames(
              'c-receipt__border',
              'c-receipt__border--bottom',
              { 'c-receipt__border--ad': receipt.receiptAds }
            )}
          />
          {!receipt.receiptAds &&
          <div className="c-receipt__footer">
            &copy; {year} Squire Technologies, Inc.<br />
            12 East 49th Street, #16-109<br />
            New York, NY 10017<br />
            <br />
            <a href="https://getsquire.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">
              Squire Privacy Policy
            </a>
            <br />
            <a href="https://getsquire.com/" target="_blank" rel="noopener noreferrer">
              <img className="u-mt" src={squireLogoMonochrome} width="25" alt="" />
            </a>
          </div>
          }
        </div>
      </div>
      }
      {receipt.receiptAds &&
      <div className="c-receipt__ad">
        <a href="https://www.forhims.com/squire" target="_blank" rel="noopener noreferrer">
          <img src={forhimsAd} alt="forhims" />
        </a>
      </div>
      }
    </div>
  );
};
ReceiptComponent.propTypes = {
  receipt: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, amount: PropTypes.number })),
    total: PropTypes.number,
    tip: PropTypes.number,
    taxAndFee: PropTypes.number,
    subtotal: PropTypes.number,
    promo: PropTypes.number,
    discount: PropTypes.number,
  }),
  loading: PropTypes.bool.isRequired,
};
ReceiptComponent.defaultProps = { receipt: {} };

const Receipt = compose(
  withRouter,
  connect(
    (state, ownProps) => ({
      receipt: getReceipt(state),
      loading: getReceiptLoading(state),
      transactionIdentifier: ownProps.params.transactionIdentifier,
      id: ownProps.params.id,
    }),
    { fetchReceipt },
  ),
  withHandlers({
    fetchReceipt: props => () => props.fetchReceipt(props.id, props.transactionIdentifier),
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchReceipt();
    },
  }),
)(ReceiptComponent);

export default Receipt;



// WEBPACK FOOTER //
// ./src/app/receipt/Receipt.jsx